import { useCallback, useEffect, useState } from 'react';
import type { DeviceDetail, DeviceMember, ShareInvite } from '@mantelpiece/shared';
import { api, type StoredSession } from '../lib/api';
import type { Guard } from '../pages/Device';

interface Props {
  readonly session: StoredSession;
  readonly device: DeviceDetail;
  readonly guard: Guard;
}

const formatDate = (ms: number): string =>
  new Date(ms).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });

const ROLE_LABEL: Record<DeviceMember['role'], string> = {
  owner: 'Owner',
  delegate: 'Delegate',
  guest: 'Guest',
};

export const PeopleTab = ({ session, device, guard }: Props) => {
  const [members, setMembers] = useState<readonly DeviceMember[]>([]);
  const [invites, setInvites] = useState<readonly ShareInvite[]>([]);
  const [label, setLabel] = useState('');
  const [role, setRole] = useState<'delegate' | 'guest'>('guest');
  const [canSend, setCanSend] = useState(true);
  const [autoAccept, setAutoAccept] = useState(false);
  const [ttl, setTtl] = useState('365');
  const [fresh, setFresh] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const load = useCallback(
    () =>
      guard(async () => {
        const [memberList, inviteList] = await Promise.all([
          api.listMembers(session.token, device.id),
          api.listInvites(session.token, device.id),
        ]);
        setMembers(memberList.members);
        setInvites(inviteList.invites);
      }),
    [device.id, guard, session.token],
  );

  useEffect(() => {
    void load();
  }, [load]);

  const create = () =>
    guard(async () => {
      const days = ttl === 'never' ? null : Number(ttl);
      const result = await api.createInvite(session.token, device.id, {
        label: label.trim() || (role === 'delegate' ? 'Household' : 'Family'),
        role,
        // A delegate's permissions are implied by the role, so the two switches
        // below only ever describe a guest.
        canSend: role === 'delegate' ? true : canSend,
        autoAccept: role === 'delegate' ? true : autoAccept,
        ttlDays: days,
      });
      setFresh(result.url);
      setCopied(false);
      setLabel('');
      await load();
    });

  const patch = (member: DeviceMember, changes: { canSend?: boolean; autoAccept?: boolean }) =>
    guard(async () => {
      await api.updateMember(session.token, device.id, member.accountId, changes);
      await load();
    });

  const openToAnyone = device.sharing.publicUpload;
  const trustsAnyone = device.sharing.autoAcceptEveryone;

  return (
    <section className="stack-loose">
      <div>
        <div className="section-head">
          <p className="eyebrow">Who this frame is shared with</p>
        </div>

        {(openToAnyone || trustsAnyone) && (
          <p className="note" style={{ marginBottom: '1rem' }}>
            {openToAnyone && trustsAnyone
              ? 'Anyone with the link can send, and everything goes straight up. The per-person switches below are being overridden.'
              : openToAnyone
                ? 'Anyone with the link can send, so the “can send” switches below are being overridden.'
                : 'Everything sent is accepted automatically, so the “goes straight up” switches below are being overridden.'}{' '}
            Change that on the Sharing tab.
          </p>
        )}

        {members.length === 0 ? (
          <p className="empty">Nobody yet.</p>
        ) : (
          <ul className="ledger">
            {members.map((member) => (
              <li key={member.accountId}>
                <span className="ledger-main">
                  <span className="ledger-name">
                    {member.name}
                    {member.accountId === session.account.id ? ' (you)' : ''}
                    <span className="pill">{ROLE_LABEL[member.role]}</span>
                  </span>
                  <span className="ledger-meta">
                    {member.email}
                    {member.role !== 'owner' ? ` · joined ${formatDate(member.joinedAt)}` : ''}
                  </span>

                  {member.role === 'guest' && (
                    <span className="switches">
                      <label className="check">
                        <input
                          type="checkbox"
                          checked={member.effective.canSend}
                          disabled={openToAnyone}
                          onChange={(event) =>
                            void patch(member, { canSend: event.target.checked })
                          }
                        />
                        Can send photos
                      </label>
                      <label className="check">
                        <input
                          type="checkbox"
                          checked={member.effective.autoAccept}
                          disabled={trustsAnyone}
                          onChange={(event) =>
                            void patch(member, { autoAccept: event.target.checked })
                          }
                        />
                        Goes straight up, no approval
                      </label>
                    </span>
                  )}
                </span>

                {member.role !== 'owner' &&
                  (member.role === 'guest' || device.permissions.canAdminister) && (
                    <button
                      type="button"
                      className="button button-danger button-small"
                      onClick={() =>
                        void guard(async () => {
                          await api.removeMember(session.token, device.id, member.accountId);
                          await load();
                        })
                      }
                    >
                      Remove
                    </button>
                  )}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div>
        <div className="section-head">
          <p className="eyebrow">New share link</p>
        </div>
        <p className="note" style={{ marginBottom: '1rem' }}>
          Whoever opens the link signs in — or signs up — and is added to this frame with the
          permissions you set here. Revoking a link also removes everyone who joined through it.
        </p>

        <div className="stack">
          <div className="row">
            <label className="field">
              <span>Who is it for</span>
              <input
                className="input"
                value={label}
                maxLength={40}
                placeholder={role === 'delegate' ? 'Sam' : 'Mum and Dad'}
                onChange={(event) => setLabel(event.target.value)}
              />
            </label>
            <label className="field" style={{ flex: '0 1 12rem' }}>
              <span>As a</span>
              <select
                className="select"
                value={role}
                onChange={(event) => setRole(event.target.value === 'delegate' ? 'delegate' : 'guest')}
                disabled={!device.permissions.canAdminister}
              >
                <option value="guest">Guest — sends photos</option>
                <option value="delegate">Delegate — helps run it</option>
              </select>
            </label>
            <label className="field" style={{ flex: '0 1 10rem' }}>
              <span>Expires</span>
              <select className="select" value={ttl} onChange={(event) => setTtl(event.target.value)}>
                <option value="30">In 30 days</option>
                <option value="365">In a year</option>
                <option value="never">Never</option>
              </select>
            </label>
          </div>

          {role === 'guest' ? (
            <>
              <label className="check">
                <input
                  type="checkbox"
                  checked={canSend}
                  onChange={(event) => setCanSend(event.target.checked)}
                />
                Allow this person to send photos
              </label>
              <label className="check">
                <input
                  type="checkbox"
                  checked={autoAccept}
                  onChange={(event) => setAutoAccept(event.target.checked)}
                />
                Auto-accept photos from this person
              </label>
            </>
          ) : (
            <p className="note">
              Delegates can do everything you can except delete the frame or change who else is a
              delegate. Only you can hand that out.
            </p>
          )}

          <div>
            <button type="button" className="button" onClick={() => void create()}>
              Create link
            </button>
          </div>
        </div>

        {fresh && (
          <div className="stack" style={{ marginTop: '1.25rem' }}>
            <p className="alert" data-tone="ok">
              Copy this now — it is not shown again.
            </p>
            <div className="copyline">
              <code>{fresh}</code>
              <button
                type="button"
                className="button button-quiet button-small"
                onClick={() => {
                  void navigator.clipboard.writeText(fresh).then(() => setCopied(true));
                }}
              >
                {copied ? 'Copied' : 'Copy'}
              </button>
            </div>
          </div>
        )}
      </div>

      <div>
        <div className="section-head">
          <p className="eyebrow">Active links</p>
        </div>
        {invites.length === 0 ? (
          <p className="empty">No share links yet.</p>
        ) : (
          <ul className="ledger">
            {invites.map((invite) => (
              <li key={invite.id}>
                <span className="ledger-main">
                  <span className="ledger-name">
                    {invite.label}
                    <span className="pill">{ROLE_LABEL[invite.role]}</span>
                  </span>
                  <span className="ledger-meta">
                    {invite.usedCount} joined · created {formatDate(invite.createdAt)}
                    {invite.expiresAt ? ` · expires ${formatDate(invite.expiresAt)}` : ''}
                  </span>
                </span>
                <button
                  type="button"
                  className="button button-danger button-small"
                  onClick={() =>
                    void guard(async () => {
                      await api.revokeInvite(session.token, device.id, invite.id);
                      await load();
                    })
                  }
                >
                  Revoke
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
};
